import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const TOKEN_KEY   = 'lm_access_token';
const REFRESH_KEY = 'lm_refresh_token';
const BRANCH_KEY  = 'lm_selected_branch';

export const tokenStorage = {
  getAccess:    ()    => localStorage.getItem(TOKEN_KEY) ?? '',
  getRefresh:   ()    => localStorage.getItem(REFRESH_KEY) ?? '',
  setTokens:    (a: string, r: string) => {
    localStorage.setItem(TOKEN_KEY, a);
    localStorage.setItem(REFRESH_KEY, r);
  },
  setAccess:    (a: string) => localStorage.setItem(TOKEN_KEY, a),
  clearTokens:  ()    => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(BRANCH_KEY);
  },
};

export const branchStorage = {
  getSelected: () => localStorage.getItem(BRANCH_KEY) ?? '',
  setSelected: (branch: string) => localStorage.setItem(BRANCH_KEY, branch),
  clearSelected: () => localStorage.removeItem(BRANCH_KEY),
};

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

// ─── Request interceptor: inject Bearer token ────────────────────────────────
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccess();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const selectedBranch = branchStorage.getSelected();
  if (selectedBranch && config.headers && !config.url?.startsWith('/auth/')) {
    config.headers['X-Branch-Name'] = selectedBranch;
    config.headers['X-Branch'] = selectedBranch;
  }

  return config;
});

// ─── Response interceptor: 401 → try refresh ────────────────────────────────
let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

function drainQueue(token: string) {
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve) => {
        refreshQueue.push(resolve);
      }).then((newToken) => {
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = tokenStorage.getRefresh();
      const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
      const newAccess: string = data.data?.accessToken ?? data.accessToken;
      tokenStorage.setAccess(newAccess);
      drainQueue(newAccess);
      original.headers.Authorization = `Bearer ${newAccess}`;
      return apiClient(original);
    } catch {
      tokenStorage.clearTokens();
      window.location.href = '/login';
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;

